"use client";
import React from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Link from "next/link";
import PageTransition from "@/components/animations/PageTransition";
import FadeInView from "@/components/animations/FadeInView";
import StaggerContainer from "@/components/animations/StaggerContainer";
import Breadcrumb from "@/components/Breadcrumb";
import ServiceQuickNav from "@/components/ServiceQuickNav";

const awsServices = [
  { icon: "🖥️", title: "AWS EC2 & Compute", desc: "Virtual servers, auto-scaling, and load balancing for business workloads of any size." },
  { icon: "🗄️", title: "AWS S3 Storage", desc: "Scalable object storage for data, backups, and media with industry-leading durability." },
  { icon: "🗃️", title: "AWS RDS & Databases", desc: "Managed relational databases with automated backups and high availability built in." },
  { icon: "🌐", title: "AWS Networking & VPC", desc: "Virtual private cloud, secure networking, and Direct Connect for enterprise connectivity." },
  { icon: "💾", title: "AWS Backup & Disaster Recovery", desc: "Automated backup policies and cross-region recovery to protect your business data." },
  { icon: "🔒", title: "AWS Security & Compliance", desc: "IAM, Shield, WAF, CloudTrail, and compliance frameworks to secure your AWS environment." },
  { icon: "📊", title: "AWS CloudWatch & Monitoring", desc: "Infrastructure monitoring, alerts, and cost optimisation across your AWS environment." },
  { icon: "🚀", title: "AWS Migration Services", desc: "Lift-and-shift, re-platforming, and modernisation migrations to AWS with minimal disruption." },
];

const migrationSteps = [
  { num: "01", title: "Discovery", desc: "Assess your current environment, workloads, and AWS migration readiness." },
  { num: "02", title: "Planning", desc: "Detailed migration plan with timelines, rollback procedures, and risk mitigation." },
  { num: "03", title: "Proof of Concept", desc: "Validate the migration approach with a controlled proof of concept environment." },
  { num: "04", title: "Migration", desc: "Execute the migration with minimal disruption to your business operations." },
  { num: "05", title: "Optimisation", desc: "Fine-tune performance, cost, and security in your new AWS environment." },
  { num: "06", title: "Ongoing Management", desc: "Continuous monitoring, management, and support for your AWS infrastructure." },
];

const benefits = [
  { icon: "📈", title: "Elastic Scalability", value: "Unlimited", desc: "Scale compute and storage resources instantly to meet any demand." },
  { icon: "🌍", title: "Global Infrastructure", value: "30+ Regions", desc: "Deploy workloads close to your customers with AWS’s global network." },
  { icon: "💰", title: "Pay-as-you-go", value: "Flexible", desc: "Only pay for the resources you use with no upfront commitments." },
  { icon: "🔒", title: "Enterprise Security", value: "Built-in", desc: "AWS’s comprehensive security services protecting your cloud environment." },
  { icon: "⏱️", title: "High Availability", value: "99.99%", desc: "Multi-AZ deployments and redundant infrastructure for maximum uptime." },
];

export default function AWSPage() {
  return (
    <PageTransition>
      <main style={{ background: "#070B14", overflowX: "hidden" }}>
        <Header />
        <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Cloud Solutions", href: "/cloud-solutions" },
            { label: "Amazon AWS" },
          ]}
        />

        {/* Hero */}
        <section className="relative min-h-[320px] md:min-h-[480px] flex items-center justify-center py-32 overflow-hidden">
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #070B14 0%, #0B1220 40%, #1a0f00 70%, #070B14 100%)" }} />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(255,153,0,0.1) 0%, transparent 70%)" }} />
          <div className="absolute bottom-1/4 left-1/3 w-80 h-80 rounded-full" style={{ background: "radial-gradient(circle, rgba(255,153,0,0.06) 0%, transparent 70%)" }} />
          <div className="relative max-w-container mx-auto px-4 sm:px-6 text-center">
            <FadeInView delay={0}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-pill mb-8" style={{ background: "rgba(255,153,0,0.1)", border: "1px solid rgba(255,153,0,0.25)" }}>
                <span className="w-2 h-2 rounded-full" style={{ background: "#FF9900" }} />
                <span className="text-[11px] font-body font-600 uppercase tracking-widest gradient-text">Amazon AWS</span>
              </div>
            </FadeInView>
            <FadeInView delay={0.15}>
              <h1 className="font-heading font-800 text-display-md md:text-display mb-6" style={{ color: "#F8FAFC", letterSpacing: "-0.03em", lineHeight: "1.05" }}>
                Amazon AWS Cloud Solutions<br />
                <span style={{ background: "linear-gradient(135deg, #FF9900, #FBBF24)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>for New Zealand Businesses</span>
              </h1>
            </FadeInView>
            <FadeInView delay={0.3}>
              <p className="text-body-lg max-w-2xl mb-10" style={{ color: "#94A3B8" }}>
                Pro IT Solutions helps businesses leverage Amazon Web Services for cloud infrastructure, hosting, storage, and managed cloud environments tailored to business needs.
              </p>
            </FadeInView>
            <FadeInView delay={0.45}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/book" className="px-6 sm:px-8 py-3.5 sm:py-4 text-[15px] font-heading font-600 rounded-pill" style={{ background: "linear-gradient(135deg, #FF9900, #F59E0B)", color: "#0B1220" }}>Book an AWS Assessment</Link>
                <Link href="#aws-services" className="px-6 sm:px-8 py-3.5 sm:py-4 text-[15px] font-heading font-500 rounded-pill btn-secondary">View AWS Services</Link>
              </div>
            </FadeInView>
            <FadeInView delay={0.6}>
              <div className="flex flex-wrap justify-center gap-6 mt-12">
                {["Amazon AWS", "AWS Partner", "Managed Cloud"]?.map((t) => (
                  <div key={t} className="flex items-center gap-2 text-[13px] font-body" style={{ color: "#64748B" }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#FF9900" }} />
                    {t}
                  </div>
                ))}
              </div>
            </FadeInView>
          </div>
        </section>

        {/* AWS Services */}
        <section id="aws-services" className="py-20 lg:py-28">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>AWS Services</h2>
              <p className="text-body-lg max-w-2xl mx-auto" style={{ color: "#94A3B8" }}>Comprehensive Amazon Web Services solutions designed and managed by AWS specialists.</p>
            </FadeInView>
            {/* Desktop grid */}
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch" staggerDelay={0.08}>
              {awsServices?.map((svc) => (
                <div key={svc?.title} className="rounded-card-lg p-6 transition-all duration-300 h-full flex flex-col" style={{ background: "rgba(255,153,0,0.04)", border: "1px solid rgba(255,153,0,0.15)" }}>
                  <div className="text-3xl mb-4">{svc?.icon}</div>
                  <h3 className="font-heading font-700 text-[24px] mb-2" style={{ color: "#F8FAFC" }}>{svc?.title}</h3>
                  <p className="text-[14px] font-body leading-[1.75]" style={{ color: "#94A3B8" }}>{svc?.desc}</p>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Migration Process */}
        <section className="py-20 lg:py-28 border-t" style={{ borderColor: "rgba(255,255,255,0.06)", background: "#0B1220" }}>
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>AWS Migration Process</h2>
            </FadeInView>
            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 items-stretch" staggerDelay={0.08}>
              {migrationSteps?.map((step) => (
                <div key={step?.num} className="rounded-card-lg p-5 text-center h-full flex flex-col" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <div className="font-heading font-800 text-[32px] mb-2" style={{ color: "rgba(255,153,0,0.4)" }}>{step?.num}</div>
                  <h3 className="font-heading font-600 text-[14px] mb-1" style={{ color: "#F8FAFC" }}>{step?.title}</h3>
                  <p className="text-[12px] font-body" style={{ color: "#94A3B8" }}>{step?.desc}</p>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 lg:py-28">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Why Amazon AWS?</h2>
            </FadeInView>
            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 items-stretch" staggerDelay={0.08}>
              {benefits?.map((b) => (
                <div key={b?.title} className="rounded-card-lg p-6 text-center h-full flex flex-col" style={{ background: "rgba(255,153,0,0.04)", border: "1px solid rgba(255,153,0,0.15)" }}>
                  <div className="text-2xl mb-3">{b?.icon}</div>
                  <div className="font-heading font-700 text-[18px] mb-1" style={{ color: "#FF9900" }}>{b?.value}</div>
                  <div className="font-heading font-600 text-[13px] mb-2" style={{ color: "#F8FAFC" }}>{b?.title}</div>
                  <p className="text-[12px] font-body" style={{ color: "#94A3B8" }}>{b?.desc}</p>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* AWS Partner Badge */}
        <section className="py-12 border-t border-b" style={{ borderColor: "rgba(255,255,255,0.06)", background: "#0B1220" }}>
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView>
              <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl" style={{ background: "rgba(255,153,0,0.1)", border: "1px solid rgba(255,153,0,0.3)" }}>🟠</div>
                <div>
                  <div className="font-heading font-700 text-[20px] mb-1" style={{ color: "#F8FAFC" }}>AWS Partner — Managed Cloud Specialists</div>
                  <p className="text-[14px] font-body" style={{ color: "#94A3B8" }}>Pro IT Solutions is an AWS Partner with expertise in cloud infrastructure, migration, and managed AWS environments for New Zealand businesses.</p>
                </div>
              </div>
            </FadeInView>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 lg:py-28">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView scale={0.95}>
              <div className="rounded-card-lg p-6 sm:p-10 lg:p-16 text-center" style={{ background: "rgba(255,153,0,0.06)", border: "1px solid rgba(255,153,0,0.2)" }}>
                <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Ready to explore AWS?</h2>
                <p className="text-body-lg mb-8" style={{ color: "#94A3B8" }}>Book a free AWS consultation and let our specialists design the right cloud architecture for your business.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link href="/book" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-600" style={{ background: "linear-gradient(135deg, #FF9900, #F59E0B)", color: "#0B1220" }}>Book a Free AWS Consultation</Link>
                  <Link href="/cloud-solutions" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-500 btn-secondary">View All Cloud Services</Link>
                </div>
              </div>
            </FadeInView>
          </div>
        </section>

        <ServiceQuickNav currentPath="/cloud-solutions/aws" />

        <Footer />
      </main>
    </PageTransition>
  );
}
